import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { FaArrowUp, FaArrowDown, FaComment, FaShare } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Utils from "../utils/Utils";
import { useTheme } from "./ThemeContext";
import ShareDialog from "./ShareDialog";

const PostActions = ({
  post,
  handleVote,
  upvotes,
  downvotes,
  sx,
  sizeMultiplier = 1,
  paddingMultiplier = 1,
  // compactBorder: only draw the divider above the button clusters, not full-width
  compactBorder = false,
}) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const iconSize = `${1 * sizeMultiplier}rem`;
  const countSize = `${0.75 * sizeMultiplier}rem`;
  const actionGap = `${0.5 * paddingMultiplier}rem`;
  const actionPx = `${0.8 * paddingMultiplier}rem`;
  const actionPy = `${0.5 * paddingMultiplier}rem`;
  const barPx = `${1.2 * paddingMultiplier}rem`;
  const barPy = `${0.8 * paddingMultiplier}rem`;

  const postUrl = `${window.location.origin}/community/${post.communityName}/${post.postId}`;

  const handleShareClick = () => {
    setOpen(true);
  };

  const handleDialogClose = () => {
    setOpen(false);
  };

  const handleDialogCopy = () => {
    navigator.clipboard.writeText(postUrl);
    setOpen(false);
  };

  // When compactBorder is true, the border sits only above each button cluster
  const clusterBorder = compactBorder ? "1px solid #1e2939" : "none";

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          px: "12px",
          py: "12px",
          // full-width border only in normal mode
          borderTop: compactBorder ? "none" : "1px solid #1e2939",
          backgroundColor: "transparent",
          ...sx,
        }}
      >
        {/* Left cluster: upvote + downvote + comments — border above cluster only */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            borderTop: clusterBorder,
            pt: compactBorder ? "10px" : 0,
            mt: compactBorder ? "-10px" : 0,
          }}
        >
        {/* Upvote */}
        <Box
          onClick={() => handleVote(post, "UPVOTE")}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            px: "12px",
            height: 32,
            borderRadius: "10px",
            backgroundColor: "rgba(0,201,80,0.1)",
            "&:hover": { backgroundColor: "rgba(0,201,80,0.18)" },
            flexShrink: 0,
          }}
        >
          <FaArrowUp style={{ color: "#05df72", fontSize: "0.75rem" }} />
          <Typography sx={{ color: "#05df72", fontWeight: 500, fontSize: "0.75rem", lineHeight: 1 }}>
            {Utils.abbreviateNumber(upvotes)}
          </Typography>
        </Box>

        {/* Downvote */}
        <Box
          onClick={() => handleVote(post, "DOWNVOTE")}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            px: "12px",
            height: 32,
            borderRadius: "10px",
            backgroundColor: "rgba(251,44,54,0.1)",
            "&:hover": { backgroundColor: "rgba(251,44,54,0.18)" },
            flexShrink: 0,
          }}
        >
          <FaArrowDown style={{ color: "#ff6467", fontSize: "0.75rem" }} />
          <Typography sx={{ color: "#ff6467", fontWeight: 500, fontSize: "0.75rem", lineHeight: 1 }}>
            {Utils.abbreviateNumber(downvotes)}
          </Typography>
        </Box>

        {/* Comments */}
        <Box
          onClick={() => navigate(`/community/${post.communityName}/${post.postId}`)}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            px: "12px",
            height: 32,
            borderRadius: "10px",
            backgroundColor: "rgba(240,177,0,0.1)",
            "&:hover": { backgroundColor: "rgba(240,177,0,0.18)" },
            flexShrink: 0,
          }}
        >
          <FaComment style={{ color: "#fdc700", fontSize: "0.75rem" }} />
          <Typography sx={{ color: "#fdc700", fontWeight: 500, fontSize: "0.75rem", lineHeight: 1 }}>
            {Utils.abbreviateNumber(post.commentCount)}
          </Typography>
        </Box>

        </Box>{/* end left cluster */}

        {/* Share — pushed to the right */}
        <Box sx={{ flex: 1 }} />
        <Box
          onClick={handleShareClick}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: "10px",
            color: "#6a7282",
            borderTop: clusterBorder,
            pt: compactBorder ? "10px" : 0,
            mt: compactBorder ? "-10px" : 0,
            "&:hover": { backgroundColor: "rgba(255,255,255,0.06)", color: "#d1d5dc" },
            flexShrink: 0,
          }}
        >
          <FaShare style={{ fontSize: "0.75rem" }} />
        </Box>
      </Box>
      <ShareDialog
        open={open}
        url={postUrl}
        handleClose={handleDialogClose}
        handleCopy={handleDialogCopy}
      />
    </>
  );
};

export default PostActions;
